import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { CheckCircle2, ExternalLink, Users } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import ROUTES from '@/routes/routes'
import type { TeamLinks } from '../api/worldGraph'
import type { TeamSetup } from '../types'

const NEW = 'new'

interface Props {
  roomId: string
  setup: TeamSetup
  onSetUp: (links: TeamLinks) => Promise<boolean>
}

function initialChoices(setup: TeamSetup): Record<string, string> {
  return Object.fromEntries(
    setup.teams.map((entry) => {
      const team = entry.team ?? entry.suggested_team
      return [entry.id, team ? String(team.id) : NEW]
    })
  )
}

/**
 * The room teams that play the roster's teams. Each roster team plays as one
 * of the room's teams; a new team is made only where none is chosen.
 */
export default function TeamsPhase({ roomId, setup, onSetUp }: Props) {
  const [choices, setChoices] = useState(() => initialChoices(setup))
  const [busy, setBusy] = useState(false)
  // Start again from the server's answer when it changes, not on every poll.
  const setupKey = JSON.stringify(setup)
  const [seenKey, setSeenKey] = useState(setupKey)
  if (seenKey !== setupKey) {
    setSeenKey(setupKey)
    setChoices(initialChoices(setup))
  }

  const roomTeams = useMemo(
    () => new Map(setup.room_teams.map((team) => [String(team.id), team])),
    [setup.room_teams]
  )
  const changed = setup.teams.some(
    (entry) => choices[entry.id] !== (entry.team ? String(entry.team.id) : NEW)
  )
  const linkedCount = setup.teams.filter((entry) => entry.team).length

  const submit = async () => {
    const links: TeamLinks = Object.fromEntries(
      setup.teams.map((entry) => [
        entry.id,
        choices[entry.id] === NEW ? null : Number(choices[entry.id]),
      ])
    )
    setBusy(true)
    if (await onSetUp(links)) toast.success("The room's teams are set up.")
    setBusy(false)
  }

  return (
    <Card className='bg-white shadow-md' data-testid='world-graph-teams'>
      <CardHeader>
        <div className='flex items-start justify-between gap-4'>
          <div>
            <CardTitle className='text-lg text-dark-blue'>
              Teams in this room
            </CardTitle>
            <CardDescription className='max-w-2xl'>
              Each played team in the roster plays as one of this room's teams.
              A new team is made only where you don't choose an existing one,
              and existing teams are never renamed or removed.
            </CardDescription>
          </div>
          {setup.complete && (
            <Badge
              variant='outline'
              className='gap-1 border-green-600/30 bg-green-50 text-green-700'
            >
              <CheckCircle2 className='h-3.5 w-3.5' aria-hidden />
              Set up
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        {setup.teams.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            The roster has no played teams. Re-open the baseline context and add
            them to the roster in its Teams &amp; Players section.
          </p>
        ) : (
          <>
            <div className='overflow-x-auto rounded-lg border'>
              <table className='w-full text-sm'>
                <thead className='bg-muted/50 text-left text-xs uppercase text-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2'>Roster team</th>
                    <th className='px-3 py-2'>Plays as</th>
                    <th className='px-3 py-2'>Players</th>
                    <th className='px-3 py-2'>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {setup.teams.map((entry) => {
                    const choice = choices[entry.id] ?? NEW
                    const chosen = roomTeams.get(choice)
                    const takenElsewhere = new Set(
                      Object.entries(choices)
                        .filter(
                          ([id, value]) => id !== entry.id && value !== NEW
                        )
                        .map(([, value]) => value)
                    )
                    return (
                      <tr key={entry.id} className='border-t align-middle'>
                        <td className='px-3 py-2 font-medium text-dark-blue'>
                          {entry.name}
                        </td>
                        <td className='px-3 py-2'>
                          <select
                            aria-label={`Room team for ${entry.name}`}
                            value={choice}
                            disabled={busy}
                            onChange={(e) =>
                              setChoices((current) => ({
                                ...current,
                                [entry.id]: e.target.value,
                              }))
                            }
                            className='h-8 w-full max-w-xs rounded-md border border-input bg-background px-2 text-sm'
                          >
                            <option value={NEW}>New team: {entry.name}</option>
                            {setup.room_teams.map((team) => (
                              <option
                                key={team.id}
                                value={String(team.id)}
                                disabled={takenElsewhere.has(String(team.id))}
                              >
                                {team.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className='px-3 py-2 text-muted-foreground'>
                          {chosen ? chosen.members : '—'}
                        </td>
                        <td className='px-3 py-2'>
                          {entry.team ? (
                            <span className='text-green-700'>Set up</span>
                          ) : (
                            <span className='text-amber-700'>
                              Not set up yet
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className='flex flex-wrap items-center justify-between gap-2'>
              <p className='inline-flex items-center gap-1 text-sm text-muted-foreground'>
                <Users className='h-4 w-4' aria-hidden />
                {setup.complete
                  ? 'Invite players and put them in these teams from the room page.'
                  : `${linkedCount} of ${setup.teams.length} teams set up.`}
              </p>
              <div className='flex flex-wrap gap-2'>
                {setup.complete && (
                  <Button variant='outline' asChild>
                    <Link to={ROUTES.PROFESSOR.ROOM_DETAILS(roomId)}>
                      <ExternalLink className='h-4 w-4' aria-hidden />
                      Room page
                    </Link>
                  </Button>
                )}
                {(!setup.complete || changed) && (
                  <Button
                    className='bg-dark-blue hover:bg-dark-blue-hovered'
                    disabled={busy}
                    onClick={submit}
                  >
                    <CheckCircle2 className='h-4 w-4' aria-hidden />
                    {setup.complete ? 'Save team choices' : 'Set up the teams'}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
